import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { useFetch } from "services/hooks/useFetch";
import { Incentive } from "./Incentive";
import { ViewIncentive } from "./ViewIncentive";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  drawerStyle,
  theme,
} from "services/constants";
import { CustomButton } from "components/CustomComponents";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Typography, Box, Paper, Drawer } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { incentivesTableHeader } from "pages/HR/Headers";
import { FilterProvider } from "pages/FilterContext";
import { ThemeProvider } from "@mui/material/styles";
import moment from "moment";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";
export const Incentives = () => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [incentiveRecords, setIncentiveRecords] = useState([]);
  const containerRef = useRef(null);

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedIncentiveData, setSelectedIncentiveData] = useState(null);
  const [handleLoader, setHandleLoader] = useState(false);

  const {
    result: incentiveData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}hrzone/incentive/incentive-template/`);

  const check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Incentive",
    "Incentive",
  );

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Incentive",
  );
  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Incentive",
  )?.sub_module_id;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (incentiveData?.length !== 0 && incentiveData && handleLoader) {
      const item = incentiveData.find(
        (d) => d.id === selectedIncentiveData?.id,
      );
      setSelectedIncentiveData(item);
    }
  }, [handleLoader, incentiveData, selectedIncentiveData?.id]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setIncentiveRecords(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleEditClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  const NewIncentiveButton = (
    <CustomButton
      actionFuntion={() => {
        if (check_access) {
          setIsCreatePanelOpen(true);
        } else {
          showToast(
            "error",
            "Access denied: You are not authorized to create incentive",
          );
        }
      }}
      actionName="New"
      typeName="button"
    />
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "incentive_saved_views",
    fullHeaders: incentivesTableHeader,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  return !handleLoader && loading ? (
    <Loader />
  ) : (
    <>
      <ThemeProvider theme={theme}>
        <FilterProvider
          domain={domain}
          userDetails={userDetails}
          subModuleId={subModuleId}
        >
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              minWidth: 0,
              backgroundColor: "#f5f5fd",
              transition: "all 0.3s ease",
            }}
          >
            <Mui.Breadcrumbs
              aria-label="breadcrumb"
              separator="›"
              sx={{ mb: 2 }}
            >
              <Typography>Payroll</Typography>
              <Typography>Pay Inputs</Typography>
              <Typography color={themes?.blueColor}>Incentive Rules</Typography>
            </Mui.Breadcrumbs>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Incentive Rules
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and Manage all incentive configurations used for payroll
                calculations
              </Typography>
            </Box>
            <Paper sx={{ width: "100%", height: "79vh" }}>
              <CustomFilter
                title={"All Incentive Rules"}
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={incentiveData}
                filteredData={incentiveRecords}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={check_access}
                newButtonComponent={
                  <>
                    {newButtonComponent}
                    {NewIncentiveButton}
                    <OpenSettingsButton group="pay-input" item="incentives" sx={{ width: 40, height: 40 }} />
                  </>
                }
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
              />

              <Mui.Grid sx={{ ml: 2, mr: 2 }}>
                <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 560 }}>
                  <Mui.Table className="border">
                    <Mui.TableHead sx={stickyHeader}>
                      <Mui.TableRow sx={tableRowSize}>
                        {visibleHeaders?.map((value) => (
                          <Mui.TableCell
                            key={value.key}
                            sx={{
                              color: themes.headLine,
                              fontWeight: "bold",
                            }}
                          >
                            {value?.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {incentiveRecords?.length === 0 ? (
                        <TableEmptyState dataLength={incentiveData} />
                      ) : (
                        incentiveRecords
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((value, index) => (
                            <React.Fragment key={index}>
                              <Mui.TableRow sx={tableRowSize}>
                                {visibleHeaders.map((head) => (
                                  <Mui.TableCell
                                    key={head.key}
                                    onClick={() => {
                                      setSelectedIncentiveData(value);
                                      setIsEditPanelOpen(true);
                                    }}
                                  >
                                    {head.key === "employee_name" ? (
                                      <Mui.Stack direction="Row">
                                        <Mui.Avatar
                                          sx={{ width: 23, height: 23 }}
                                          alt={value?.employee_name
                                            ?.charAt(0)
                                            ?.toUpperCase()}
                                          src={value?.image_url}
                                        />
                                        &nbsp;
                                        {value?.employee_name}
                                      </Mui.Stack>
                                    ) : head.key === "status" ? (
                                      <Mui.Box sx={displayFlexItem}>
                                        <StatusBadge
                                          color={value?.color_code}
                                          label={value?.status || "N/A"}
                                        />
                                      </Mui.Box>
                                    ) : (
                                      (value[head.key] ?? "N/A")
                                    )}
                                  </Mui.TableCell>
                                ))}
                              </Mui.TableRow>
                            </React.Fragment>
                          ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                </Mui.TableContainer>
                <Box sx={{ mt: 1 }}>
                  {incentiveRecords?.length > 25 && (
                    <Mui.TablePagination
                      className="custom-pagination"
                      component="div"
                      rowsPerPageOptions={[25, 50, 75, 100]}
                      count={incentiveRecords?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </Box>
              </Mui.Grid>
            </Paper>

            <Drawer
              anchor="right"
              open={isCreatePanelOpen || isEditPanelOpen}
              onClose={() => {
                isCreatePanelOpen
                  ? handleCreateClosePanel()
                  : handleEditClosePanel();
              }}
              sx={drawerStyle}
            >
              {isCreatePanelOpen && (
                <Incentive
                  onClose={() => setIsCreatePanelOpen(false)}
                  reCallApi={reCallApi}
                  selectedIncentiveData={[]}
                  setHandleLoader={setHandleLoader}
                />
              )}

              {isEditPanelOpen && (
                <ViewIncentive
                  onClose={() => setIsEditPanelOpen(false)}
                  selectedIncentiveData={selectedIncentiveData}
                  reCallApi={reCallApi}
                  setHandleLoader={setHandleLoader}
                />
              )}
            </Drawer>
          </Box>
        </FilterProvider>
      </ThemeProvider>
    </>
  );
};
